// Per-trade P&L distribution histogram. Each bar is a 1% bucket of trade returns,
// coloured red for negative buckets and green for positive. Dashed vertical lines mark
// the 25th and 75th percentiles. Mirrors TradingView's "P&L Distribution" panel.

import type { BacktestResponseDto } from "../../api/types";
import { histogramBuckets, percentile } from "./backtestMath";

interface Props {
  result: BacktestResponseDto;
  height?: number;
}

function pickReturns(result: BacktestResponseDto): number[] {
  if (result.strategy && result.strategy.returns.length > 0) return [...result.strategy.returns];
  if (result.merged && result.merged.returns.length > 0) return [...result.merged.returns];
  const out: number[] = [];
  if (result.long) out.push(...result.long.returns);
  if (result.short) out.push(...result.short.returns);
  return out;
}

export function PnlHistogram({ result, height = 200 }: Props) {
  const returns = pickReturns(result);
  if (returns.length === 0) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        No trades to histogram.
      </div>
    );
  }

  const buckets = histogramBuckets(returns, 1);
  const maxCount = buckets.reduce((m, b) => (b.count > m ? b.count : m), 0);
  const p25 = percentile(returns, 25);
  const p75 = percentile(returns, 75);

  // SVG layout.
  const W = 360, H = height;
  const padX = 32, padTop = 16, padBottom = 32;
  const baseline = H - padBottom;
  const barAreaWidth = W - padX * 2;
  const barWidth = Math.max(barAreaWidth / buckets.length - 1, 2);

  // X scale: map bucket lower edge → x position. Use first/last bucket as the range.
  const xMin = buckets[0].bucket;
  const xMax = buckets[buckets.length - 1].bucket + 1; // include the right edge of the last bucket
  const xScale = (v: number) => padX + ((v - xMin) / (xMax - xMin)) * barAreaWidth;
  const yScale = (count: number) => (maxCount === 0 ? baseline : baseline - (count / maxCount) * (baseline - padTop));

  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>P&L Distribution</div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" width="100%" height={H} style={{ display: "block" }}>
        {/* Baseline + zero-line emphasis */}
        <line x1={padX} x2={W - padX} y1={baseline} y2={baseline} stroke="#30363d" strokeWidth={1} />
        <line x1={xScale(0)} x2={xScale(0)} y1={padTop} y2={baseline} stroke="#30363d" strokeWidth={1} strokeDasharray="2 2" />
        {/* Percentile bands */}
        {Number.isFinite(p25) && (
          <line x1={xScale(p25)} x2={xScale(p25)} y1={padTop} y2={baseline} stroke="#f85149" strokeWidth={1} strokeDasharray="4 3" />
        )}
        {Number.isFinite(p75) && (
          <line x1={xScale(p75)} x2={xScale(p75)} y1={padTop} y2={baseline} stroke="#3fb950" strokeWidth={1} strokeDasharray="4 3" />
        )}
        {/* Bars */}
        {buckets.map((b) => {
          const x = xScale(b.bucket);
          const y = yScale(b.count);
          const h = baseline - y;
          const color = b.bucket < 0 ? "#f85149" : "#3fb950";
          return <rect key={b.bucket} x={x} y={y} width={barWidth} height={h} fill={color} opacity={0.85} />;
        })}
        {/* X-axis labels every ~4th bucket */}
        {buckets.map((b, i) => {
          if (i % Math.max(1, Math.floor(buckets.length / 10)) !== 0) return null;
          return (
            <text key={`x-${b.bucket}`} x={xScale(b.bucket)} y={H - 12} fill="#8b949e" fontSize={10} textAnchor="middle">
              {b.bucket >= 0 ? `+${b.bucket}` : b.bucket}%
            </text>
          );
        })}
      </svg>
      <div style={{ fontSize: 10, color: "#8b949e", marginTop: 4 }}>
        Dashed lines = 25th (red) and 75th (green) percentile bands. Buckets are 1% wide.
      </div>
    </div>
  );
}
